import { computed, ref, watch, type Ref } from "vue";
import dayjs from "dayjs";

// Define the expected structure of data
interface ProviderData {
  is_deliverable: boolean;
  distance?: number;
  max_dispatch_time?: number;
}

// Define a reusable function that returns only finalTAT
export const useCalculateTAT = (data: Ref<ProviderData | null>, category: Ref<string | null>) => {
  
  const finalTAT = ref<string | null>(null);

  // Computed property to determine if the category is hyperlocal
  const isHyperLocalCategory = computed(() => {
    return category.value ? ["grocery", "f&b"].includes(category.value.toLowerCase()) : false;
  });

  const calculateTAT = () => {
    if (!data.value || !category.value) {
      
      console.error("Missing required data for serviceability check.");
      finalTAT.value = null;
      return;
    }

    const isServiceable = data.value.is_deliverable;
    const isIntercityOrder = data.value.distance !== undefined && data.value.distance >= 10000 && data.value.distance <= 15000;

    let baseDispatchTime = dayjs.duration(data.value.max_dispatch_time || 1800, "seconds").asMinutes();
    let adjustedDispatchTime = baseDispatchTime;

    if (isHyperLocalCategory.value && isServiceable && data.value.distance !== undefined && data.value.distance <= 5000) {
      adjustedDispatchTime += 10; // Hyperlocal adjustment
    } else if (isServiceable && isIntercityOrder) {
      adjustedDispatchTime += 30; // Intercity serviceable adjustment
    } else if (isServiceable && data.value.distance !== undefined && data.value.distance >= 5000 && data.value.distance <= 10000) {
      adjustedDispatchTime += 20; // Polygon/Custom Coverage adjustment
    } else {
      finalTAT.value = null; // No TAT for non-serviceable stores
      return;
    }

    finalTAT.value = formatTime(adjustedDispatchTime);
  };

  // Helper function to format time
  const formatTime = (minutes: number): string => {
    if (minutes >= 60) {
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hrs} hrs ${mins} mins` : `${hrs} hrs`;
    }
    return `${minutes} mins`;
  };

  // Watch for changes in `data` or `category` and recalculate TAT
  watch([data, category], calculateTAT, { deep: true, immediate: true });

  // Return only finalTAT as a computed value
  return computed(() => finalTAT.value);
};
